import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Loader2, UserCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RACE_OPTIONS = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic or Latino",
  "Middle Eastern or North African (MENA)",
  "Native Hawaiian or Other Pacific Islander",
  "White",
  "Mixed Race"
];

export const DemographicsDialog = ({ open, onComplete }) => {
  const { token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [race, setRace] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!zipCode.trim()) {
      toast.error('Please enter your zip code');
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      toast.error('Please enter a valid zip code');
      return;
    }
    if (!race) {
      toast.error('Please select your race/ethnicity');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/demographics`,
        { zip_code: zipCode.trim(), race },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thank you for completing your profile!');
      if (refreshUser) {
        await refreshUser();
      }
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save demographics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px] [&>button]:hidden" data-testid="demographics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-display">
            <UserCircle className="w-6 h-6 text-primary" />
            Complete Your Profile
          </DialogTitle>
          <DialogDescription>
            Help us understand our community better. This information helps us ensure Greenish serves the people who need it most.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="zipCode">What is your zip code? *</Label>
            <Input
              id="zipCode"
              placeholder="e.g., 11216"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              maxLength={10}
              data-testid="zip-code-input"
            />
          </div>
          
          <div className="space-y-3">
            <Label>How do you identify? *</Label>
            <p className="text-xs text-muted-foreground">Please select one option</p>
            <RadioGroup value={race} onValueChange={setRace} className="space-y-2">
              {RACE_OPTIONS.map((option) => (
                <div key={option} className="flex items-center space-x-3">
                  <RadioGroupItem 
                    value={option} 
                    id={option.replace(/\s+/g, '-').toLowerCase()}
                    data-testid={`race-${option.replace(/\s+/g, '-').toLowerCase()}`}
                  />
                  <Label 
                    htmlFor={option.replace(/\s+/g, '-').toLowerCase()}
                    className="font-normal cursor-pointer"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          
          <Button
            type="submit"
            className="w-full rounded-full bg-primary"
            disabled={loading}
            data-testid="submit-demographics-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue to Greenish'
            )}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Your information is kept private and only used in aggregate to improve our service.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
